import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonIcon, IonItem, IonRadio, IonRadioGroup } from "@ionic/react"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { ToolfinderTrocutParameterForm } from "./FormTypes"
import { useEffect, useState } from "react"
import { arrowDownOutline, createOutline } from "ionicons/icons"
import { InputAdornment, TextField, Typography } from "@mui/material"
import { mapTrocutMethod } from "./FormHelpers"
import { useTranslation } from "react-i18next"

interface TrocutParameterFormProps {
    isFormShowing: boolean,
    submitForm: (data: any) => void,
    strategy: string | undefined,
    editBtnCallback: () => void,
    formData: ToolfinderTrocutParameterForm | undefined
}

const TrocutParameterForm: React.FC<TrocutParameterFormProps> = ({ isFormShowing, submitForm, strategy, editBtnCallback, formData }: TrocutParameterFormProps) => {
    const [data, setData] = useState<ToolfinderTrocutParameterForm | undefined>()
    const { control, reset, handleSubmit, formState: { errors }, watch, setValue, getValues } = useForm<ToolfinderTrocutParameterForm>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            ap: "",
            method: "inAndOut",
            min_r: "",
        }
    })
    const { t } = useTranslation();

    const selectedMethod = watch("method");

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            reset()
        }
        return () => { ignore = true }
    }, [strategy, reset])

    useEffect(() => {
        if (formData === undefined) {
            setData(undefined)
        }
    }, [formData])

    const onSubmit: SubmitHandler<ToolfinderTrocutParameterForm> = data => {
        setData(data)
        submitForm(data)

    }

    const onMethodChange = (val: string) => {
        setValue("method", val);
        if (val === "onlyOut" && getValues("min_r") !== "") {
            setValue("min_r", "")
        }
    }

    if (isFormShowing) {
        return (
            <IonCard>
                <IonCardHeader>
                    <IonCardTitle>{t("toolfinder.Parameter")}</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                    <form>
                        {/* a_p */}
                        <div>
                            <Controller
                                control={control}
                                name="ap"
                                rules={{
                                    required: { value: true, message: t("Feld darf nicht leer sein") },
                                    min: { value: 0.1, message: t("toolfinder.TrocutParameterForm.minAp", { param: "0.1 mm" }) },
                                    max: { value: 100, message: t("toolfinder.TrocutParameterForm.maxAp", { param: "100 mm" }) },
                                }}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        variant="standard"
                                        label={<Typography>a<sub>p</sub></Typography>}
                                        value={value}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        type="number"
                                        style={{ width: "100%" }}
                                        slotProps={{
                                            input: {
                                                endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                                            }
                                        }}
                                    />
                                )}
                            />

                            {errors.ap && <div className="error">{errors.ap.message}</div>}
                        </div>

                        {/* Bearbeitung */}
                        <div style={{ marginTop: 10 }}>
                            <Typography>{t("toolfinder.TrocutParameterForm.Bearbeitung")}</Typography>
                            <Controller
                                control={control}
                                name="method"
                                rules={{
                                    required: { value: true, message: t("Feld darf nicht leer sein") },
                                }}
                                render={({ field: { value } }) => (
                                    <IonRadioGroup value={value} onIonChange={({ detail: { value } }) => onMethodChange(value)}>
                                        <IonItem>
                                            <IonRadio value="inAndOut" labelPlacement="end" justify="start">
                                                {t("toolfinder.TrocutParameterForm.inAndOut")}
                                            </IonRadio>
                                        </IonItem>
                                        <IonItem>
                                            <IonRadio value="onlyOut" labelPlacement="end" justify="start">
                                                {t("toolfinder.TrocutParameterForm.onlyOut")}
                                            </IonRadio>
                                        </IonItem>
                                    </IonRadioGroup>
                                )}
                            />
                            {errors.method && <div className="error">{errors.method.message}</div>}
                        </div>

                        {/* Innenradius */}
                        <div style={{ marginTop: 10 }}>
                            <Controller
                                control={control}
                                name="min_r"
                                rules={{
                                    required: { value: selectedMethod === "inAndOut", message: t("Feld darf nicht leer sein") },
                                    min: { value: 1, message: t("toolfinder.TrocutParameterForm.valueTooSmall") },
                                    max: { value: 20, message: t("toolfinder.TrocutParameterForm.valueTooBig") },
                                }}
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <TextField
                                        variant="standard"
                                        label={t("toolfinder.TrocutParameterForm.minR")}
                                        value={value}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        type="number"
                                        disabled={selectedMethod === "onlyOut"}
                                        style={{ width: "100%" }}
                                        slotProps={{
                                            input: {
                                                endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                                            }
                                        }}
                                    />
                                )}
                            />

                            {/* @ts-ignore */}
                            {errors.min_r && <div className="error">{errors.min_r.message}</div>}
                        </div>

                        {/* Submit */}
                        <IonButton style={{ marginTop: 24 }} onClick={handleSubmit(onSubmit)} expand="block">
                            <IonIcon slot="start" icon={arrowDownOutline} />{t("toolfinder.TrocutParameterForm.submit")}
                        </IonButton>
                    </form>
                </IonCardContent>
            </IonCard>
        )
    } else {
        if (data) {
            return (
                <IonCard color="light" >
                    <IonCardContent>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                            <div>
                                <div>
                                    <b>a<sub>p</sub>:</b> {Number(data.ap).toLocaleString()} mm <br />
                                    <b>{t("toolfinder.TrocutParameterForm.Bearbeitung")}:</b> {mapTrocutMethod(data.method)} <br />
                                    {data.method === "inAndOut" && <span><b>{t("toolfinder.TrocutParameterForm.minR")}:</b> {data.min_r} mm</span>}
                                </div>
                            </div>
                            <div>
                                <IonButton fill="outline" onClick={editBtnCallback}>
                                    <IonIcon icon={createOutline} slot="icon-only" />
                                </IonButton>
                            </div>
                        </div>
                    </IonCardContent>
                </IonCard>
            )
        } else {
            return null
        }
    }
}

export default TrocutParameterForm